import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardActionArea,
  Chip,
  Stack,
  useTheme,
  useMediaQuery,
  Paper,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  SentimentSatisfied,
  Tag,
  EmojiEmotions,
  SmartToy,
  ReadMore,
  Psychology,
  Summarize,
  Label,
  CompareArrows,
  ArrowForward,
  TrendingUp,
  Speed,
  Security,
  Palette,
  GitHub,
  Twitter,
  LinkedIn,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

import AboutUs from './AboutUs';
import { useTheme as useCustomTheme } from '../theme/ThemeProvider';

const HomePage = () => {
  const theme = useTheme();
  const { darkMode } = useCustomTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const tools = [
    {
      title: 'Sentiment Analyzer',
      description: 'Analyze the emotional tone of your text with advanced AI-powered sentiment detection.',
      icon: <SentimentSatisfied sx={{ fontSize: 40 }} />,
      path: '/SentimentAnalyzer',
      color: '#6366f1',
      features: ['Emotion Detection', 'Tone Analysis', 'Real-time Results'],
    },
    {
      title: 'Hashtag Generator',
      description: 'Generate optimized hashtags to maximize your social media reach and engagement.',
      icon: <Tag sx={{ fontSize: 40 }} />,
      path: '/HashtagsAnalyzer',
      color: '#06b6d4',
      features: ['Trending Hashtags', 'Reach Optimization', 'Category Filtering'],
    },
    {
      title: 'Emoji Suggester',
      description: 'Transform your text with smart emoji suggestions to make it more engaging and fun.',
      icon: <EmojiEmotions sx={{ fontSize: 40 }} />,
      path: '/EmojisAnalyzer',
      color: '#f59e0b',
      features: ['Smart Suggestions', 'Context Awareness', 'Customization'],
    },
    {
      title: 'AI Text Detector',
      description: 'Identify AI-generated content with high accuracy using advanced detection algorithms.',
      icon: <SmartToy sx={{ fontSize: 40 }} />,
      path: '/AITextAnalyzer',
      color: '#ef4444',
      features: ['High Accuracy', 'Multiple Models', 'Detailed Analysis'],
    },
    {
      title: 'Readability Checker',
      description: 'Evaluate and improve the readability of your content for better audience engagement.',
      icon: <ReadMore sx={{ fontSize: 40 }} />,
      path: '/ReadabilityAnalyzer',
      color: '#10b981',
      features: ['Multiple Metrics', 'Improvement Tips', 'Audience Targeting'],
    },
    {
      title: 'Text Summarizer',
      description: 'Transform long texts into concise, meaningful summaries using advanced AI technology.',
      icon: <Summarize sx={{ fontSize: 40 }} />,
      path: '/TextSummarizationAnalyzer',
      color: '#f97316',
      features: ['AI-Powered', 'Fast Processing', 'High Compression'],
    },
    {
      title: 'Keyword Extractor',
      description: 'Extract meaningful keywords and keyphrases from your text using advanced AI models.',
      icon: <Label sx={{ fontSize: 40 }} />,
      path: '/KeywordExtractionAnalyzer',
      color: '#22c55e',
      features: ['AI-Powered', 'Batch Processing', 'Keyword Analysis'],
    },
    {
      title: 'Semantic Similarity',
      description: 'Analyze semantic relationships between texts using advanced AI embeddings and similarity algorithms.',
      icon: <CompareArrows sx={{ fontSize: 40 }} />,
      path: '/SemanticSimilarityAnalyzer',
      color: '#ec4899',
      features: ['Embedding Analysis', 'Similarity Search', 'Text Clustering'],
    },
    {
      title: 'Post Splitter',
      description: 'Break down long content into optimal chunks for different social media platforms.',
      icon: <Psychology sx={{ fontSize: 40 }} />,
      path: '/PostSplitter',
      color: '#8b5cf6',
      features: ['Platform Optimization', 'Smart Splitting', 'Character Limits'],
    },
  ];

  const features = [
    {
      icon: <Speed sx={{ fontSize: 32 }} />,
      title: 'Lightning Fast',
      description: 'Get results instantly with our optimized algorithms and cloud infrastructure.',
    },
    {
      icon: <Security sx={{ fontSize: 32 }} />,
      title: 'Privacy First',
      description: 'Your data is processed securely and never stored permanently.',
    },
    {
      icon: <TrendingUp sx={{ fontSize: 32 }} />,
      title: 'AI Powered',
      description: 'Leverage cutting-edge AI models for accurate and reliable text analysis.',
    },
    {
      icon: <Palette sx={{ fontSize: 32 }} />,
      title: 'Modern Design',
      description: 'Enjoy a beautiful, responsive interface that works on all devices.',
    },
  ];

  const handleToolClick = (path) => {
    navigate(path);
  };

  return (
    <>
      <Helmet>
        <title>TextSimpler - AI-Powered Text Analysis Tools</title>
        <meta name="description" content="Transform your text with AI-powered tools for sentiment analysis, hashtag generation, emoji suggestions, and more. Professional, fast, and secure." />
        <meta name="keywords" content="text analysis, sentiment analysis, hashtag generator, emoji suggester, AI text detector, readability checker, post splitter" />
        <meta name="author" content="TextSimpler Team" />
        <link rel="canonical" href="https://www.textsimpler.com/" />
        <meta property="og:title" content="TextSimpler - AI-Powered Text Analysis Tools" />
        <meta property="og:description" content="Transform your text with AI-powered tools for sentiment analysis, hashtag generation, emoji suggestions, and more." />
        <meta property="og:url" content="https://www.textsimpler.com/" />
        <meta property="og:type" content="website" />
      </Helmet>

      

      {/* Hero Section */}
      <Box
        sx={{
          minHeight: '90vh',
          display: 'flex',
          alignItems: 'center',
          background: darkMode
            ? 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)'
            : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 50%, #cbd5e1 100%)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Background Elements */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `radial-gradient(circle at 20% 80%, ${theme.palette.primary.main}15 0%, transparent 50%),
                        radial-gradient(circle at 80% 20%, ${theme.palette.secondary.main}15 0%, transparent 50%)`,
          }}
        />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                variant={isMobile ? 'h3' : 'h1'}
                component="h1"
                sx={{
                  fontWeight: 700,
                  mb: 3,
                  background: 'linear-gradient(135deg, #6366f1 0%, #06b6d4 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  lineHeight: 1.2,
                }}
              >
                Transform Your Text with AI
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  color: 'text.secondary',
                  mb: 4,
                  lineHeight: 1.6,
                  fontWeight: 400,
                }}
              >
                Professional text analysis tools powered by advanced AI. Analyze sentiment, generate hashtags, 
                suggest emojis, and much more with our comprehensive suite of tools.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate('/SentimentAnalyzer')}
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                    background: 'linear-gradient(135deg, #6366f1 0%, #06b6d4 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #4f46e5 0%, #0891b2 100%)',
                    },
                  }}
                >
                  Get Started
                  <ArrowForward sx={{ ml: 1 }} />
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => document.getElementById('tools').scrollIntoView({ behavior: 'smooth' })}
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                    borderColor: 'primary.main',
                    color: 'primary.main',
                    '&:hover': {
                      borderColor: 'primary.dark',
                      backgroundColor: 'primary.main',
                      color: 'white',
                    },
                  }}
                >
                  Explore Tools
                </Button>
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                }}
              >
                <Paper
                  elevation={darkMode ? 8 : 4}
                  sx={{
                    p: 4,
                    borderRadius: 4,
                    background: darkMode ? 'rgba(30, 41, 59, 0.8)' : 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(20px)',
                    border: `1px solid ${theme.palette.divider}`,
                    maxWidth: 400,
                    width: '100%',
                  }}
                >
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    Quick Demo
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
                    Try our sentiment analyzer with a sample text to see the power of our AI tools.
                  </Typography>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() => navigate('/SentimentAnalyzer')}
                    sx={{
                      background: 'linear-gradient(135deg, #6366f1 0%, #06b6d4 100%)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #4f46e5 0%, #0891b2 100%)',
                      },
                    }}
                  >
                    Try Sentiment Analysis
                  </Button>
                </Paper>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Box sx={{ py: 8, backgroundColor: 'background.default' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            align="center"
            sx={{ mb: 6, fontWeight: 700 }}
          >
            Why Choose TextSimpler?
          </Typography>
          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    textAlign: 'center',
                    p: 3,
                    transition: 'transform 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                    },
                  }}
                >
                  <Box sx={{ color: 'primary.main', mb: 2 }}>
                    {feature.icon}
                  </Box>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {feature.description}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Tools Section */}
      <Box id="tools" sx={{ py: 8, backgroundColor: 'background.paper' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            align="center"
            sx={{ mb: 6, fontWeight: 700 }}
          >
            Our AI-Powered Tools
          </Typography>
          <Grid container spacing={4}>
            {tools.map((tool, index) => (
              <Grid item xs={12} sm={6} lg={4} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    transition: 'all 0.3s ease-in-out',
                    cursor: 'pointer',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: theme.shadows[8],
                    },
                  }}
                  onClick={() => handleToolClick(tool.path)}
                >
                  <CardActionArea sx={{ height: '100%', p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Box sx={{ color: tool.color, mr: 2 }}>
                        {tool.icon}
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {tool.title}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      {tool.description}
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {tool.features.map((feature, featureIndex) => (
                        <Chip
                          key={featureIndex}
                          label={feature}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.75rem' }}
                        />
                      ))}
                    </Stack>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          py: 8,
          background: darkMode
            ? 'linear-gradient(135deg, #1e293b 0%, #334155 100%)'
            : 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h3"
            align="center"
            sx={{ mb: 3, fontWeight: 700 }}
          >
            Ready to Transform Your Text?
          </Typography>
          <Typography
            variant="h6"
            align="center"
            sx={{ mb: 4, color: 'text.secondary' }}
          >
            Join thousands of users who trust TextSimpler for their text analysis needs.
          </Typography>
          <Box sx={{ textAlign: 'center' }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/SentimentAnalyzer')}
              sx={{
                px: 6,
                py: 2,
                fontSize: '1.2rem',
                background: 'linear-gradient(135deg, #6366f1 0%, #06b6d4 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #4f46e5 0%, #0891b2 100%)',
                },
              }}
            >
              Start Analyzing Now
              <ArrowForward sx={{ ml: 1 }} />
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Footer Social Links */}
      <Box sx={{ py: 4, backgroundColor: 'background.default', borderTop: `1px solid ${theme.palette.divider}` }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
            <Tooltip title="Follow us on Twitter">
              <IconButton
                sx={{
                  color: 'text.secondary',
                  '&:hover': { color: '#1da1f2' },
                }}
              >
                <Twitter />
              </IconButton>
            </Tooltip>
            <Tooltip title="Connect on LinkedIn">
              <IconButton
                sx={{
                  color: 'text.secondary',
                  '&:hover': { color: '#0077b5' },
                }}
              >
                <LinkedIn />
              </IconButton>
            </Tooltip>
            <Tooltip title="View on GitHub">
              <IconButton
                sx={{
                  color: 'text.secondary',
                  '&:hover': { color: '#333' },
                }}
              >
                <GitHub />
              </IconButton>
            </Tooltip>
          </Box>
        </Container>
      </Box>

      <AboutUs />
    </>
  );
};

export default HomePage;
